import React from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { List, ListItem } from '../List';
import Headline5 from '../Typography/Heading/Headline5';
import Body2 from '../Typography/Body/Body2';
import { Grid, GridCell } from '../Grid';
import Headline3 from '../Typography/Heading/Headline3';
import { Breadcrumbs } from '../Breadcrumb';
import breakpoints from '../Theme/breakpoints';
import SearchField from '../SearchField';
import colors from '../Theme/colors';

export const LogoContainer = styled.div`
  width: 102px;
  padding-right: 32px;
`;

export const CourseList = styled(List)`
  && {
    background-color: ${colors.white};
  }
`;

export const StyledBody2 = styled(Body2)`
  && {
    margin: 0;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    -webkit-line-clamp: 2;
    /* autoprefixer: ignore next */
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const StyledListItem = styled(ListItem)`
  && {
    padding: 24px 16px;
    height: auto;

    @media (min-width: ${breakpoints.md}px) {
        padding: 24px;
    }
  }

  a {
    display: flex;
    align-items: center;
  }
`;

export const CenterTitle = styled(Headline3)`
  && {
    display: block;
    margin: 32px 16px;

    @media only screen and (min-width: ${breakpoints.xssm}px) {
      text-align: center;
    }
  }
`;

export const Container = styled.div`
  padding: 0 16px;
  display: block;

  @media only screen and (min-width: ${breakpoints.md}px) {
    display: flex;
    padding: 0;
  }
`;

export const StyledGridCell = styled(GridCell)`
  width: 100%;
  margin: 0 auto;

  .MuiTextField-root {
    width: 100%;
    margin-bottom: 16px;
  }
`;

export const StyledSearchField = styled(SearchField)`
  max-width: 840px;
`;

export const SelectMenuContainer = styled.div`
  width: 100%;

  @media (min-width: ${breakpoints.md}px) {
    margin-left: 16px;
    width: 40%;
  }
`;

export const ListItemContent = styled.div`
  display: flex;
  align-items: center;
  height: auto;
`;

const courseListItemPropertyTypes = {
  children: PropTypes.node,
  logo: PropTypes.node,
  title: PropTypes.string,
  courseInfo: PropTypes.string,
  introduction: PropTypes.string,
};

type CourseListItemProperties = InferProps<typeof courseListItemPropertyTypes>;

export const CourseListItem = ({
  children,
  logo,
  title,
  courseInfo,
  introduction,
}: CourseListItemProperties) => (
  <StyledListItem className="fu-list-item-content" showDivider>
    {children ?? (
      <ListItemContent className="fu-list-item-content">
        {logo}
        <div style={{ display: 'block' }}>
          <Headline5 tag="h2">
            {title}
          </Headline5>
          <StyledBody2>
            {courseInfo}
          </StyledBody2>
          <StyledBody2>
            {introduction}
          </StyledBody2>
        </div>
      </ListItemContent>
    )}
  </StyledListItem>
);

CourseListItem.propTypes = courseListItemPropertyTypes;

CourseListItem.defaultProps = {
  children: undefined,
  logo: undefined,
  title: undefined,
  courseInfo: undefined,
  introduction: undefined,
};

const coursesPagePropertyTypes = {
  title: PropTypes.string.isRequired,
  breadcrumbs: PropTypes.arrayOf(PropTypes.node).isRequired,
  children: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  categorySelect: PropTypes.node,
  keywords: PropTypes.string,
};

type CoursesPageProperties = InferProps<typeof coursesPagePropertyTypes>;

const CoursesPage = ({
  breadcrumbs,
  title,
  children,
  keywords,
  onChange,
  onClear,
  categorySelect,
}: CoursesPageProperties) => (
  <Grid>
    <GridCell padding="0 20px">
      <Breadcrumbs
        crumbs={breadcrumbs}
      />
      <CenterTitle tag="h1">
        {title}
      </CenterTitle>
    </GridCell>
    <StyledGridCell padding="16px">
      <Container>
        <StyledSearchField
          id="courses-search"
          label="Search for a course"
          value={keywords}
          onChange={onChange}
          onClear={onClear}
        />
        {categorySelect}
      </Container>
      {children}
    </StyledGridCell>
  </Grid>
);

CoursesPage.propTypes = coursesPagePropertyTypes;

CoursesPage.defaultProps = {
  children: undefined,
  keywords: undefined,
  categorySelect: undefined,
};

export default CoursesPage;
